/**
 * UTM landing capture — the first-party half of ad-click → order attribution.
 *
 * Every ad push stamps utm_campaign = the Diztree campaign _id onto its
 * landing URL (utils/marketing/promotable.ts · utmForAd). This plugin stores
 * the utm_* set of the CURRENT visit in the `dz_attr` first-party cookie
 * (last-click wins, 30 days) so the checkout endpoint can copy it onto the
 * Order (orderAttribution.ts) and the ROAS view can join spend to real
 * revenue per campaign/target.
 *
 * CONSENT-GATED: the cookie is only written once the visitor grants the
 * `marketing` category. The typical flow — land with utm params, banner
 * appears, visitor accepts — works because the captured params are held in
 * memory and flushed the moment consent flips to granted. Declined consent →
 * nothing is stored, the order simply carries no attribution (the same
 * direction as the pixel gating: fewer measured conversions, lawfully).
 *
 * Staff hosts never capture — ads land buyers on shop.* / the apex only.
 */
const STAFF_HOST_PREFIXES = ['admin.', 'creator.', 'labels.', 'scouts.', 'superadmin.', 'lnk.']

export default defineNuxtPlugin(() => {
    if (typeof window === 'undefined') return
    const host = window.location.host
    if (STAFF_HOST_PREFIXES.some(p => host.startsWith(p))) return

    const { state: consent } = useCookieConsent()
    const attrCookie = useCookie<any>('dz_attr', {
        maxAge: 60 * 60 * 24 * 30,
        sameSite: 'lax',
    })

    let pending: any = null

    function captureFrom(query: Record<string, any>) {
        const source = query.utm_source
        const campaign = query.utm_campaign
        if (!source && !campaign) return
        const str = (v: any, max = 200) => (typeof v === 'string' && v ? v.slice(0, max) : null)
        const captured = {
            source: str(source, 100),
            medium: str(query.utm_medium, 100),
            campaign: str(campaign, 100),
            content: str(query.utm_content, 100),
            term: str(query.utm_term, 200),
            at: new Date().toISOString(),
        }
        if (consent.value.marketing) attrCookie.value = captured
        else pending = captured
    }

    // Landing hit + SPA navigations (a later ad click can navigate in-app).
    const route = useRoute()
    captureFrom(route.query as any)
    useRouter().afterEach((to) => captureFrom(to.query as any))

    // Banner accepted after landing → flush the held capture.
    watch(() => consent.value.marketing, (granted) => {
        if (granted && pending) {
            attrCookie.value = pending
            pending = null
        }
    })
})
